// components/DestinationModal.tsx
import React from 'react';
import { FaMapPin, FaTimes, FaUserFriends, FaPhone, FaSave } from 'react-icons/fa';
import { DestinationForm } from '../types';

interface DestinationModalProps {
  isOpen: boolean;
  editingId: number | null;
  formData: DestinationForm;
  isPickingLocation: boolean;
  error: string | null;
  onClose: () => void;
  onFormChange: (data: Partial<DestinationForm>) => void;
  onPickLocation: () => void;
  onSubmit: () => void;
}

export const DestinationModal: React.FC<DestinationModalProps> = ({
  isOpen,
  editingId,
  formData,
  isPickingLocation,
  error,
  onClose,
  onFormChange,
  onPickLocation,
  onSubmit,
}) => {
  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 bg-black bg-opacity-50 z-[2000] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <FaMapPin className="text-green-500" />
              {editingId !== null ? 'Edit Destination' : 'Where do you want to go?'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FaUserFriends className="inline mr-2" />
                Destination Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => onFormChange({ name: e.target.value })}
                placeholder="e.g., Office, Home, Restaurant..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FaPhone className="inline mr-2" />
                Contact Information *
              </label>
              <input
                type="text"
                value={formData.contact}
                onChange={(e) => onFormChange({ contact: e.target.value })}
                placeholder="e.g., Phone number or email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FaMapPin className="inline mr-2" />
                Location *
              </label>
              <div className="space-y-2">
                {formData.lat !== 0 && formData.lng !== 0 ? (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-blue-700">
                      📍 Selected: {formData.lat.toFixed(6)}, {formData.lng.toFixed(6)}
                    </p>
                    <button
                      onClick={onPickLocation}
                      className="mt-1 text-xs text-blue-600 hover:text-blue-800"
                    >
                      Change location on map
                    </button>
                  </div>
                ) : (
                  <div>
                    <button
                      onClick={onPickLocation}
                      className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                    >
                      <FaMapPin /> Click on map to pick location
                    </button>
                    {isPickingLocation && (
                      <p className="text-sm text-blue-600 mt-2 animate-pulse">
                        👆 Click anywhere on the map to select location
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onSubmit}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
              >
                <FaSave />
                {editingId !== null ? 'Update' : 'Save Destination'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};